import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserCheck, 
  CheckCircle, 
  XCircle, 
  Calendar, 
  MessageSquare,
  AlertCircle,
  Gift,
  Star,
  Package,
  Clock
} from 'lucide-react';
import { userService } from '../services/userService';
import { rewardService } from '../services/rewardService';
import { firestoreService } from '../services/firestore';
import { useNotification } from '../contexts/NotificationContext';
import { formatDateForDisplay } from '../utils/dateUtils';
import { ApproverRequest } from '../types/user';
import { RedeemedReward } from '../types/reward';
import { auth } from '../services/auth';
import type { Reward } from '../types/reward';
import type { Task } from '../types/task';

const Container = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  border: 1px solid #f1f5f9;
`;

const Title = styled.h3`
  margin: 0 0 32px 0;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterButton = styled(motion.button)<{ active: boolean }>`
  padding: 12px 20px;
  border: 2px solid ${(props: { active: boolean }) => props.active ? '#F57C00' : '#e2e8f0'};
  border-radius: 8px;
  background: ${(props: { active: boolean }) => props.active ? '#F57C00' : 'white'};
  color: ${(props: { active: boolean }) => props.active ? 'white' : '#374151'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const RequestList = styled.div`
  display: grid;
  gap: 16px;
`;

const RequestCard = styled(motion.div)<{ type: 'approver' | 'reward' | 'proposal' | 'mission' | 'verification' }>`
  padding: 24px;
  border-radius: 12px;
  border: 2px solid ${(props: { type: 'approver' | 'reward' | 'proposal' | 'mission' | 'verification' }) => {
    switch (props.type) {
      case 'approver': return '#F57C00';
      case 'reward': return '#10b981';
      case 'mission': return '#F57C00';
      case 'verification': return '#F57C00';
      default: return '#F57C00';
    }
  }};
  background: ${(props: { type: 'approver' | 'reward' | 'proposal' | 'mission' | 'verification' }) => {
    switch (props.type) {
      case 'approver': return '#fef3c7';
      case 'reward': return '#f0fdf4';
      case 'mission': return '#fef3c7';
      case 'verification': return '#fef3c7';
      default: return '#fef3c7';
    }
  }};
  position: relative;
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const RequestInfo = styled.div`
  flex: 1;
`;

const RequestTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const RequestDescription = styled.p`
  color: #64748b;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

const RequestType = styled.div<{ type: 'approver' | 'reward' | 'proposal' | 'mission' | 'verification' }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props: { type: 'approver' | 'reward' | 'proposal' | 'mission' | 'verification' }) => {
    switch (props.type) {
      case 'approver': return '#E65100';
      case 'reward': return '#059669';
      case 'mission': return '#E65100';
      case 'verification': return '#E65100';
      default: return '#E65100';
    }
  }};
  padding: 6px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid currentColor;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RequestDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
`;

const RequestDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const CreditsBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #8B4513;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CategoryBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #e2e8f0;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RequestActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled(motion.button)<{ variant: 'approve' | 'reject' | 'accept' | 'decline' }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${(props: { variant: 'approve' | 'reject' | 'accept' | 'decline' }) => {
    switch (props.variant) {
      case 'approve':
      case 'accept':
        return `
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          &:hover { 
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          }
        `;
      case 'reject':
      case 'decline':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          &:hover { 
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 40px;
  color: #64748b;
`;

const InfoSection = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #F57C00;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #E65100;
`;

const CreditInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;
const CreditInputBox = styled.input`
  width: 80px;
  padding: 10px 12px;
  border: 2px solid #F57C00;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  background: #fff7ed;
  color: #E65100;
  font-weight: 700;
  transition: border-color 0.2s;
  &:focus {
    outline: none;
    border-color: #E65100;
    background: #fff;
  }
`;
const CreditButton = styled.button`
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  color: white;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  font-size: 20px;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: linear-gradient(135deg, #E65100 0%, #F57C00 100%);
  }
`;

interface ApproverRequestsProps {
  onClose: () => void;
}

type RequestType = 'approver' | 'reward' | 'proposal' | 'mission' | 'verification';
type FilterType = 'all' | 'approver' | 'reward' | 'proposal' | 'mission' | 'verification';

const ApproverRequests: React.FC<ApproverRequestsProps> = ({ onClose }) => {
  const [approverRequests, setApproverRequests] = useState<ApproverRequest[]>([]);
  const [rewardRequests, setRewardRequests] = useState<RedeemedReward[]>([]);
  const [rewardProposals, setRewardProposals] = useState<Reward[]>([]);
  const [missionRequests, setMissionRequests] = useState<Task[]>([]);
  const [missionVerifications, setMissionVerifications] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [costInput, setCostInput] = useState<{ [rewardId: string]: string }>({});
  const [missionCreditsInput, setMissionCreditsInput] = useState<{ [missionId: string]: string }>({});
  const { showSuccess, showError } = useNotification();

  // Initialize mission credits input when mission requests are loaded
  useEffect(() => {
    const initialCredits: { [missionId: string]: string } = {};
    missionRequests.forEach(mission => {
      initialCredits[mission.id] = String(mission.credits);
    });
    setMissionCreditsInput(prev => ({ ...prev, ...initialCredits }));
  }, [missionRequests]);

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    // Set default value for cost input for proposals
    rewardProposals.forEach((proposal) => {
      if (!costInput[proposal.id]) {
        setCostInput(prev => ({ ...prev, [proposal.id]: '200' }));
      }
    });
    // eslint-disable-next-line
  }, [rewardProposals]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      
      // Load approver requests and reward requests
      const [approverReqs, rewardReqs] = await Promise.all([
        userService.getPendingApproverRequests(),
        rewardService.getPendingApprovalRequests()
      ]);
      setApproverRequests(approverReqs);
      setRewardRequests(rewardReqs);

      // Try to load reward proposals (will return empty array if user is not an approver)
      try {
        const proposals = await rewardService.getPendingRewardProposals();
        setRewardProposals(proposals);
      } catch (error) {
        console.error('Error loading reward proposals:', error);
        setRewardProposals([]);
      }

      // Try to load mission requests (will return empty array if user is not an approver)
      try {
        const missions = await firestoreService.getPendingMissionsForApprovers();
        console.log('Loaded mission requests:', missions);
        setMissionRequests(missions);
      } catch (error) {
        console.error('Error loading mission requests:', error);
        setMissionRequests([]);
      }

      // Try to load mission verifications (will return empty array if user is not an approver)
      try {
        const verifications = await firestoreService.getPendingMissionVerifications();
        console.log('Loaded mission verifications:', verifications);
        setMissionVerifications(verifications);
      } catch (error) {
        console.error('Error loading mission verifications:', error);
        setMissionVerifications([]);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      showError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToApproverRequest = async (requestId: string, status: 'accepted' | 'rejected', message?: string) => {
    try {
      setProcessingRequest(requestId);
      await userService.respondToApproverRequest(requestId, status, message);
      showSuccess(`Approver request ${status} successfully!`);
      await loadRequests(); // Reload data
    } catch (error) {
      console.error('Error responding to approver request:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to respond to request';
      showError(errorMessage);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleApproveRewardRequest = async (rewardId: string) => {
    try {
      setProcessingRequest(rewardId);
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      await rewardService.approveRewardUsage(rewardId, user.uid);
      showSuccess('Reward usage confirmed successfully!');
      await loadRequests(); // Reload data
    } catch (error) {
      console.error('Error approving reward request:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve request';
      showError(errorMessage);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRewardRequest = async (rewardId: string) => {
    try {
      setProcessingRequest(rewardId);
      
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      await rewardService.rejectRewardUsage(rewardId, user.uid);
      showSuccess('Reward usage rejected successfully!');
      await loadRequests(); // Reload data
    } catch (error) {
      console.error('Error rejecting reward request:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject request';
      showError(errorMessage);
    } finally {
      setProcessingRequest(null);
    }
  };

  // Approve reward proposal (set cost, mark as approved)
  const handleApproveRewardProposal = async (reward: Reward) => {
    const cost = parseInt(costInput[reward.id], 10);
    if (!cost || cost <= 0) {
      showError('Please enter a valid credit cost.');
      return;
    }
    setProcessingRequest(reward.id);
    try {
      await rewardService.updateRewardProposal(reward.id, {
        status: 'approved',
        cost,
        available: true,
        updatedAt: new Date()
      });
      if (reward.requestedBy) {
        await rewardService.sendProposalNotification(reward.requestedBy, 'approved', reward.name);
      }
      showSuccess('Reward proposal approved!');
      setCostInput(prev => ({ ...prev, [reward.id]: '' }));
      await loadRequests();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to approve proposal');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Reject reward proposal
  const handleRejectRewardProposal = async (reward: Reward) => {
    const rejectionReason = prompt('Please provide a reason for rejection (optional):');
    if (rejectionReason === null) return; // User cancelled
    
    setProcessingRequest(reward.id);
    try {
      await rewardService.updateRewardProposal(reward.id, {
        status: 'rejected',
        available: false,
        rejectionReason: rejectionReason || undefined,
        updatedAt: new Date()
      });
      if (reward.requestedBy) {
        await rewardService.sendProposalNotification(reward.requestedBy, 'rejected', reward.name);
      }
      showSuccess('Reward proposal rejected.');
      setCostInput(prev => ({ ...prev, [reward.id]: '' }));
      await loadRequests();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reject proposal');
    } finally {
      setProcessingRequest(null);
    }
  };

  const getFilteredRequests = () => {
    const allRequests = [
      ...approverRequests.map(req => ({ ...req, type: 'approver' as const })),
      ...rewardRequests.map(req => ({ ...req, type: 'reward' as const })),
      ...rewardProposals.map(req => ({ ...req, type: 'proposal' as const })),
      ...missionRequests.map(req => ({ ...req, type: 'mission' as const })),
      ...missionVerifications.map(req => ({ ...req, type: 'verification' as const }))
    ];

    if (filter === 'all') return allRequests;
    return allRequests.filter(req => req.type === filter);
  };

  const filteredRequests = getFilteredRequests();

  if (loading) {
    return (
      <Container>
        <div>Loading requests...</div>
      </Container>
    );
  }

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Title>
        <UserCheck />
        Requests
      </Title>

      <InfoSection>
        <AlertCircle size={16} />
        <span>Review and respond to approver requests and reward usage confirmation requests.</span>
      </InfoSection>

      <FilterSection>
        <FilterButton
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <MessageSquare />
          All ({filteredRequests.length})
        </FilterButton>
        <FilterButton
          active={filter === 'approver'}
          onClick={() => setFilter('approver')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <UserCheck />
          Approver Requests ({approverRequests.length})
        </FilterButton>
        <FilterButton
          active={filter === 'mission'}
          onClick={() => setFilter('mission')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Package />
          Mission Requests ({missionRequests.length})
        </FilterButton>
        <FilterButton
          active={filter === 'verification'}
          onClick={() => setFilter('verification')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <CheckCircle />
          Mission Verifications ({missionVerifications.length})
        </FilterButton>
        <FilterButton
          active={filter === 'reward'}
          onClick={() => setFilter('reward')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Gift />
          Reward Confirmations ({rewardRequests.length})
        </FilterButton>
        <FilterButton
          active={filter === 'proposal'}
          onClick={() => setFilter('proposal')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Star />
          Reward Proposals ({rewardProposals.length})
        </FilterButton>
      </FilterSection>

      {filteredRequests.length === 0 ? (
        <EmptyState>
          <MessageSquare size={48} />
          <h4>No Pending Requests</h4>
          <p>You don't have any pending requests to review.</p>
        </EmptyState>
      ) : (
        <RequestList>
          <AnimatePresence>
            {filteredRequests.map((request) => (
              <RequestCard
                key={`${request.type}-${request.id}`}
                type={request.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <RequestHeader>
                  <RequestInfo>
                    <RequestTitle>
                      {request.type === 'approver' 
                        ? `Approver Request from ${request.requesterName}`
                        : request.type === 'proposal'
                        ? (request as Reward).name
                        : request.type === 'mission'
                        ? (request as Task).title
                        : request.type === 'verification'
                        ? (request as Task).title
                        : (request as RedeemedReward).rewardName
                      }
                    </RequestTitle>
                    <RequestDescription>
                      {request.type === 'approver'
                        ? `${request.requesterName} (${request.requesterEmail}) is requesting you to be their credit approver.`
                        : request.type === 'proposal'
                        ? (request as Reward).description
                        : request.type === 'mission'
                        ? (request as Task).description
                        : request.type === 'verification'
                        ? `Mission completion verification requested for: ${(request as Task).description}`
                        : (request as RedeemedReward).rewardDescription
                      }
                    </RequestDescription>
                  </RequestInfo>
                  
                  <RequestType type={request.type}>
                    {request.type === 'approver' ? <UserCheck size={12} /> : request.type === 'proposal' ? <Star size={12} /> : request.type === 'mission' ? <Package size={12} /> : request.type === 'verification' ? <CheckCircle size={12} /> : <Gift size={12} />}
                    {request.type === 'approver' ? 'Approver Request' : request.type === 'proposal' ? 'Reward Proposal' : request.type === 'mission' ? 'Mission Request' : request.type === 'verification' ? 'Mission Verification' : 'Reward Confirmation'}
                  </RequestType>
                </RequestHeader>

                <RequestDetails>
                  <RequestDetail>
                    <Calendar />
                    {request.type === 'approver' 
                      ? `Requested: ${formatDateForDisplay((request as ApproverRequest).requestedAt)}`
                      : request.type === 'proposal'
                      ? `Proposed: ${formatDateForDisplay((request as Reward).createdAt)}`
                      : request.type === 'mission'
                      ? `Requested: ${formatDateForDisplay((request as Task).createdAt)}`
                      : request.type === 'verification'
                      ? `Verification Requested: ${formatDateForDisplay((request as Task).updatedAt)}`
                      : `Redeemed: ${formatDateForDisplay((request as RedeemedReward).redeemedAt)}`
                    }
                  </RequestDetail>
                  
                  {request.type === 'reward' && (request as RedeemedReward).approvalRequestedAt && (
                    <RequestDetail>
                      <MessageSquare />
                      Requested: {formatDateForDisplay((request as RedeemedReward).approvalRequestedAt!)}
                    </RequestDetail>
                  )}
                  
                  {request.type === 'reward' && (
                    <>
                      <CreditsBadge>
                        <Star size={12} />
                        {(request as RedeemedReward).cost} Credits
                      </CreditsBadge>
                      <CategoryBadge>
                        {(request as RedeemedReward).category}
                      </CategoryBadge>
                    </>
                  )}
                  
                  {request.type === 'proposal' && (
                    <>
                      <CategoryBadge>
                        {(request as Reward).category}
                      </CategoryBadge>
                    </>
                  )}

                  {request.type === 'mission' && (
                    <>
                      <CategoryBadge>
                        {(request as Task).category}
                      </CategoryBadge>
                    </>
                  )}
                </RequestDetails>

                {request.type === 'reward' && (request as RedeemedReward).approvalNotes && (
                  <div style={{ marginTop: '12px', padding: '12px', background: 'white', borderRadius: '6px' }}>
                    <strong>User Notes:</strong>
                    <p style={{ margin: '8px 0 0 0', color: '#64748b' }}>{(request as RedeemedReward).approvalNotes}</p>
                  </div>
                )}

                <RequestActions>
                  {request.type === 'approver' ? (
                    <>
                      <ActionButton
                        variant="accept"
                        onClick={() => handleRespondToApproverRequest(request.id, 'accepted')}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <CheckCircle size={16} />
                        {processingRequest === request.id ? 'Accepting...' : 'Accept'}
                      </ActionButton>
                      <ActionButton
                        variant="decline"
                        onClick={() => handleRespondToApproverRequest(request.id, 'rejected')}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <XCircle size={16} />
                        {processingRequest === request.id ? 'Declining...' : 'Decline'}
                      </ActionButton>
                    </>
                  ) : request.type === 'reward' ? (
                    <>
                      <ActionButton
                        variant="approve"
                        onClick={() => handleApproveRewardRequest(request.id)}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <CheckCircle size={16} />
                        {processingRequest === request.id ? 'Confirming...' : 'Confirm'}
                      </ActionButton>
                      <ActionButton
                        variant="reject"
                        onClick={() => handleRejectRewardRequest(request.id)}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <XCircle size={16} />
                        {processingRequest === request.id ? 'Rejecting...' : 'Reject'}
                      </ActionButton>
                    </>
                  ) : request.type === 'proposal' ? (
                    <>
                      <div style={{ width: '100%', marginBottom: 12 }}>
                        <label style={{ fontWeight: 600 }}>Set Credit Cost to Redeem *</label>
                        <CreditInputContainer>
                          <CreditButton
                            type="button"
                            onClick={() => {
                              const current = parseInt(costInput[request.id] || '200', 10);
                              setCostInput(prev => ({ ...prev, [request.id]: String(Math.max(50, current - 50)) }));
                            }}
                            disabled={processingRequest === request.id}
                          >
                            -
                          </CreditButton>
                          <CreditInputBox
                            type="number"
                            min={50}
                            step={50}
                            value={costInput[request.id] || '200'}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              let val = parseInt(e.target.value, 10);
                              if (isNaN(val)) val = 200;
                              setCostInput(prev => ({ ...prev, [request.id]: String(val) }));
                            }}
                            disabled={processingRequest === request.id}
                            required
                          />
                          <CreditButton
                            type="button"
                            onClick={() => {
                              const current = parseInt(costInput[request.id] || '200', 10);
                              setCostInput(prev => ({ ...prev, [request.id]: String(current + 50) }));
                            }}
                            disabled={processingRequest === request.id}
                          >
                            +
                          </CreditButton>
                        </CreditInputContainer>
                      </div>
                      <ActionButton
                        variant="approve"
                        onClick={() => handleApproveRewardProposal(request as Reward)}
                        disabled={processingRequest === request.id || !costInput[request.id] || parseInt(costInput[request.id], 10) <= 0}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <CheckCircle size={16} />
                        {processingRequest === request.id ? 'Approving...' : 'Approve'}
                      </ActionButton>
                      <ActionButton
                        variant="reject"
                        onClick={() => handleRejectRewardProposal(request as Reward)}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <XCircle size={16} />
                        {processingRequest === request.id ? 'Rejecting...' : 'Reject'}
                      </ActionButton>
                    </>
                  ) : request.type === 'mission' ? (
                    <>
                      <div style={{ width: '100%', marginBottom: 12 }}>
                        <label style={{ fontWeight: 600 }}>Set Mission Credits *</label>
                        <CreditInputContainer>
                          <CreditButton
                            type="button"
                            onClick={() => {
                              const current = parseInt(missionCreditsInput[request.id] || String((request as Task).credits), 10);
                              setMissionCreditsInput(prev => ({ ...prev, [request.id]: String(Math.max(50, current - 50)) }));
                            }}
                            disabled={processingRequest === request.id}
                          >
                            -
                          </CreditButton>
                          <CreditInputBox
                            type="number"
                            min={50}
                            step={50}
                            value={missionCreditsInput[request.id] || String((request as Task).credits)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              let val = parseInt(e.target.value, 10);
                              if (isNaN(val)) val = (request as Task).credits;
                              setMissionCreditsInput(prev => ({ ...prev, [request.id]: String(val) }));
                            }}
                            disabled={processingRequest === request.id}
                            required
                          />
                          <CreditButton
                            type="button"
                            onClick={() => {
                              const current = parseInt(missionCreditsInput[request.id] || String((request as Task).credits), 10);
                              setMissionCreditsInput(prev => ({ ...prev, [request.id]: String(current + 50) }));
                            }}
                            disabled={processingRequest === request.id}
                          >
                            +
                          </CreditButton>
                        </CreditInputContainer>
                      </div>
                      <ActionButton
                        variant="approve"
                        onClick={async () => {
                          const credits = parseInt(missionCreditsInput[request.id] || String((request as Task).credits), 10);
                          if (!credits || credits <= 0) {
                            showError('Please enter a valid credit cost.');
                            return;
                          }
                          setProcessingRequest(request.id);
                          try {
                            await firestoreService.approveMission(request.id, credits);
                            showSuccess('Mission request approved!');
                            setMissionCreditsInput(prev => ({ ...prev, [request.id]: '' }));
                            await loadRequests();
                          } catch (err) {
                            showError(err instanceof Error ? err.message : 'Failed to approve mission request');
                          } finally {
                            setProcessingRequest(null);
                          }
                        }}
                        disabled={processingRequest === request.id || !missionCreditsInput[request.id] || parseInt(missionCreditsInput[request.id], 10) <= 0}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <CheckCircle size={16} />
                        {processingRequest === request.id ? 'Approving...' : 'Approve'}
                      </ActionButton>
                      <ActionButton
                        variant="reject"
                        onClick={async () => {
                          const rejectionReason = prompt('Please provide a reason for rejection (optional):');
                          if (rejectionReason === null) return; // User cancelled
                          setProcessingRequest(request.id);
                          try {
                            await firestoreService.rejectMission(request.id, rejectionReason);
                            showSuccess('Mission request rejected.');
                            setMissionCreditsInput(prev => ({ ...prev, [request.id]: '' }));
                            await loadRequests();
                          } catch (err) {
                            showError(err instanceof Error ? err.message : 'Failed to reject mission request');
                          } finally {
                            setProcessingRequest(null);
                          }
                        }}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <XCircle size={16} />
                        {processingRequest === request.id ? 'Rejecting...' : 'Reject'}
                      </ActionButton>
                    </>
                  ) : request.type === 'verification' ? (
                    <>
                      <ActionButton
                        variant="approve"
                        onClick={async () => {
                          setProcessingRequest(request.id);
                          try {
                            await firestoreService.verifyMissionCompletion(request.id, true);
                            showSuccess('Mission verification approved!');
                            await loadRequests();
                          } catch (err) {
                            showError(err instanceof Error ? err.message : 'Failed to approve mission verification');
                          } finally {
                            setProcessingRequest(null);
                          }
                        }}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <CheckCircle size={16} />
                        {processingRequest === request.id ? 'Approving...' : 'Approve Verification'}
                      </ActionButton>
                      <ActionButton
                        variant="reject"
                        onClick={async () => {
                          const rejectionReason = prompt('Please provide a reason for rejection (optional):');
                          if (rejectionReason === null) return; // User cancelled
                          setProcessingRequest(request.id);
                          try {
                            await firestoreService.verifyMissionCompletion(request.id, false, rejectionReason);
                            showSuccess('Mission verification rejected.');
                            await loadRequests();
                          } catch (err) {
                            showError(err instanceof Error ? err.message : 'Failed to reject mission verification');
                          } finally {
                            setProcessingRequest(null);
                          }
                        }}
                        disabled={processingRequest === request.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <XCircle size={16} />
                        {processingRequest === request.id ? 'Rejecting...' : 'Reject Verification'}
                      </ActionButton>
                    </>
                  ) : null}
                </RequestActions>
              </RequestCard>
            ))}
          </AnimatePresence>
        </RequestList>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px' }}>
        <ActionButton
          variant="approve"
          onClick={onClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Close
        </ActionButton>
      </div>
    </Container>
  );
};

export default ApproverRequests; 